/**
 * The on-screen controls for phones and tablets (`docs/mobile.md`): DOM elements over the canvas that
 * feed the input package's TouchReader, plus the small platform helpers a phone needs (fullscreen,
 * the install prompt, iOS and standalone detection).
 *
 * The layer covers the screen with `touch-action: none` and takes every pointer, so the browser never
 * scrolls, zooms, selects, or opens a callout under a thumb. The left thumb drives a stationary stick;
 * the right thumb holds GRAB, JUMP, or TUCK and leans by sliding. Pause, restart, and fullscreen sit
 * in the top-left corner, away from both thumbs and from the link counter at the top right. The
 * pressed states follow pointer events; the bar context changes the release label and hint.
 */

import type { TouchButton, TouchReader } from "@lache/input";
import { landscapePoint, landscapeSize, requestLandscape } from "./orientation";

const BUTTONS: readonly TouchButton[] = ["grab", "jump", "tuck"];
const LABELS: Record<TouchButton, string> = { grab: "GRAB", jump: "JUMP", tuck: "TUCK" };

const ICON_PAUSE =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>';
const ICON_RESTART =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5a7 7 0 1 1-6.6 4.7" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round"/><path d="M3.2 4.2 5.6 10l5.6-2.6z"/></svg>';
const ICON_FULLSCREEN =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';

type FullscreenDocument = Document & {
  webkitFullscreenEnabled?: boolean;
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
};
type FullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
};

/** Whether this browser can put an element in fullscreen (Android and desktop yes, iPhone Safari no). */
export function fullscreenAvailable(): boolean {
  const doc = document as FullscreenDocument;
  return doc.fullscreenEnabled === true || doc.webkitFullscreenEnabled === true;
}

export function isFullscreen(): boolean {
  const doc = document as FullscreenDocument;
  return (doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null) !== null;
}

/** Enter fullscreen and hold the phone sideways where the browser allows it. Must run inside a user gesture. */
export function enterFullscreen(): void {
  if (isFullscreen() || !fullscreenAvailable()) {
    void requestLandscape();
    return;
  }
  const el = document.documentElement as FullscreenElement;
  try {
    const request = el.requestFullscreen
      ? el.requestFullscreen({ navigationUI: "hide" })
      : el.webkitRequestFullscreen?.();
    if (request && typeof request.then === "function") request.then(requestLandscape).catch(() => {});
    else void requestLandscape();
  } catch {
    // Refused (not a user gesture, or blocked): the game plays in the page.
  }
}

export function exitFullscreen(): void {
  const doc = document as FullscreenDocument;
  try {
    const exit = doc.exitFullscreen ? doc.exitFullscreen() : doc.webkitExitFullscreen?.();
    if (exit && typeof exit.then === "function") exit.catch(() => {});
  } catch {
    // Already out.
  }
}

/** iPhone, iPod, or iPad (an iPad reports itself as a Mac with a touch screen). */
export function isIos(): boolean {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

/** Launched from the home screen (an installed web app), where there is no browser bar to hide. */
export function isStandalone(): boolean {
  const nav = navigator as Navigator & { standalone?: boolean };
  return (
    nav.standalone === true ||
    matchMedia("(display-mode: standalone)").matches ||
    matchMedia("(display-mode: fullscreen)").matches
  );
}

/** A phone or tablet whose main pointer is a finger. */
export function prefersTouch(): boolean {
  return matchMedia("(pointer: coarse)").matches && navigator.maxTouchPoints > 0;
}

export interface TouchHost {
  /** The fullscreen button was pressed. */
  toggleFullscreen(): void;
}

export class TouchControls {
  readonly el: HTMLDivElement;
  private readonly stickEl: HTMLDivElement;
  private readonly stickBase: HTMLDivElement;
  private readonly knobEl: HTMLDivElement;
  private readonly buttonEls = {} as Record<TouchButton, HTMLDivElement>;
  private readonly fullscreenButton: HTMLButtonElement;
  private readonly lacheHint: HTMLDivElement;
  private barLabel = false;
  private ladderContext = false;
  private shown = false;

  constructor(
    parent: HTMLElement,
    private readonly reader: TouchReader,
    host: TouchHost,
  ) {
    const el = document.createElement("div");
    el.className = "touch-controls";
    el.hidden = true;
    this.el = el;

    this.stickEl = document.createElement("div");
    this.stickEl.className = "touch-stick";
    const base = document.createElement("div");
    base.className = "touch-stick-base";
    this.stickBase = base;
    for (const [cls, text] of [
      ["touch-stick-up", "▲ jump"],
      ["touch-stick-down", "▼ tuck"],
    ] as const) {
      const mark = document.createElement("span");
      mark.className = cls;
      mark.textContent = text;
      base.appendChild(mark);
    }
    this.knobEl = document.createElement("div");
    this.knobEl.className = "touch-stick-knob";
    this.stickEl.append(base, this.knobEl);
    el.appendChild(this.stickEl);

    for (const b of BUTTONS) {
      const button = document.createElement("div");
      button.className = `touch-button touch-${b}`;
      const label = document.createElement("span");
      label.textContent = LABELS[b];
      button.appendChild(label);
      el.appendChild(button);
      this.buttonEls[b] = button;
    }
    this.lacheHint = document.createElement("div");
    this.lacheHint.className = "touch-lache-hint";
    this.lacheHint.hidden = true;
    el.appendChild(this.lacheHint);

    const top = document.createElement("div");
    top.className = "touch-top";
    const makeButton = (cls: string, icon: string, title: string, action: () => void): HTMLButtonElement => {
      const b = document.createElement("button");
      b.type = "button";
      b.className = cls;
      b.innerHTML = icon;
      b.title = title;
      b.setAttribute("aria-label", title);
      b.addEventListener("click", (e) => {
        e.preventDefault();
        action();
      });
      top.appendChild(b);
      return b;
    };
    makeButton("touch-pause", ICON_PAUSE, "Pause", () => reader.tap("pause", performance.now()));
    makeButton("touch-restart", ICON_RESTART, "Restart", () => reader.tap("restart", performance.now()));
    this.fullscreenButton = makeButton("touch-fullscreen", ICON_FULLSCREEN, "Fullscreen", () =>
      host.toggleFullscreen(),
    );
    el.appendChild(top);
    parent.appendChild(el);

    const onTop = (e: Event): boolean => (e.target as Element | null)?.closest?.(".touch-top") != null;
    el.addEventListener("pointerdown", (e) => {
      if (onTop(e) || (e.pointerType === "mouse" && e.button !== 0)) return;
      this.press(e);
    });
    el.addEventListener("pointermove", (e) => {
      const p = landscapePoint(e.clientX, e.clientY);
      reader.move(e.pointerId, p.x, p.y, performance.now());
      this.draw();
    });
    const lift = (e: PointerEvent): void => {
      reader.up(e.pointerId);
      this.draw();
    };
    el.addEventListener("pointerup", lift);
    el.addEventListener("pointercancel", lift);
    el.addEventListener("lostpointercapture", lift);
    // Touch events still drive iOS gestures (double-tap zoom, the text loupe, the callout) even with
    // pointer events handled; cancel them everywhere except on the corner buttons, which need their click.
    const cancel = (e: TouchEvent): void => {
      if (!onTop(e)) e.preventDefault();
    };
    el.addEventListener("touchstart", cancel, { passive: false });
    el.addEventListener("touchmove", cancel, { passive: false });
    el.addEventListener("contextmenu", (e) => e.preventDefault());
    window.addEventListener("resize", () => this.layout());
    window.addEventListener("lacheviewportchange", () => {
      reader.cancelAll();
      this.layout();
      this.draw();
    });
  }

  get visible(): boolean {
    return this.shown;
  }

  /** Adopt the first finger that revealed these controls; its event still targets the old canvas. */
  adoptPointer(e: PointerEvent): void {
    if (!this.shown || this.el.contains(e.target as Node | null)) return;
    const target = document.elementFromPoint(e.clientX, e.clientY);
    // Corner buttons need their native click; overlays above the controls retain their own input.
    if (!target || !this.el.contains(target) || target.closest(".touch-top")) return;
    this.press(e);
  }

  private press(e: PointerEvent): void {
    e.preventDefault();
    try {
      this.el.setPointerCapture(e.pointerId);
    } catch {
      // A pointer that is already gone cannot be captured; its up still arrives.
    }
    const p = landscapePoint(e.clientX, e.clientY);
    this.reader.down(e.pointerId, p.x, p.y, performance.now());
    this.draw();
  }

  /** Show or hide the controls. Hiding lifts every finger, so nothing stays held behind a menu. */
  show(v: boolean): void {
    if (v === this.shown) return;
    this.shown = v;
    this.el.hidden = !v;
    this.reader.cancelAll();
    if (v) this.layout();
    this.draw();
  }

  /** Context changes the label, never the button's position or hit target. */
  update(onBar: boolean, onLadder: boolean, betweenBars: boolean): void {
    this.reader.setBarContext(onBar, onLadder);
    if (onBar !== this.barLabel || onLadder !== this.ladderContext) {
      this.barLabel = onBar;
      this.ladderContext = onLadder;
      this.buttonEls.jump.firstElementChild!.textContent = onBar ? "RELEASE" : "JUMP";
      if (this.shown) this.draw();
    }
    const hint = !this.reader.isHeld("grab")
      ? ""
      : onLadder
        ? this.reader.awaitingGripReturn
          ? "Slide back to GRAB for the next duck."
          : "Slide GRAB to TUCK just before the swing bottom."
        : !this.reader.config.lacheAssist || (!onBar && !betweenBars)
          ? ""
          : this.reader.awaitingGripReturn
            ? "Keep your thumb down. Slide back to GRAB."
            : onBar
              ? "Slide GRAB to RELEASE without lifting your thumb."
              : "Keep holding GRAB to catch the next bar.";
    if (this.lacheHint.textContent !== hint) this.lacheHint.textContent = hint;
    this.lacheHint.hidden = hint === "";
  }

  /** Measure the buttons into the reader's circles and size the stick; runs on show and on every resize. */
  layout(): void {
    const r = this.reader;
    const size = landscapeSize();
    r.width = size.width;
    r.height = size.height;
    this.el.style.setProperty("--stick-radius", `${r.config.stickRadius}px`);
    this.fullscreenButton.hidden = !fullscreenAvailable() || isStandalone();
    if (!this.shown) return;
    const stickRect = this.stickBase.getBoundingClientRect();
    const stickCenter = landscapePoint(
      stickRect.left + stickRect.width / 2,
      stickRect.top + stickRect.height / 2,
    );
    r.setStickCenter(stickCenter.x, stickCenter.y);
    for (const b of BUTTONS) {
      const rect = this.buttonEls[b].getBoundingClientRect();
      const circle = r.buttons[b];
      const center = landscapePoint(rect.left + rect.width / 2, rect.top + rect.height / 2);
      circle.x = center.x;
      circle.y = center.y;
      circle.r = Math.min(rect.width, rect.height) / 2;
    }
    this.draw();
  }

  private draw(): void {
    const s = this.reader.stick;
    this.stickEl.classList.toggle("held", s.held);
    this.knobEl.style.transform = s.held ? `translate(${s.dx}px, ${s.dy}px)` : "";
    for (const b of BUTTONS) this.buttonEls[b].classList.toggle("held", this.reader.isHeld(b));
  }
}
